import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { Mail, Lock, User, AlertCircle } from 'lucide-react'
import { signUpWithEmail, signInWithEmail, signInWithGoogle, resetPassword } from '../services/auth'
import { Button, Card, Input, AnimatedLine } from '../components/UI'

const AuthView = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  
  const [mode, setMode] = useState(searchParams.get('mode') === 'signup' ? 'signup' : 'signin')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
  })

  // Update mode from URL params
  useEffect(() => {
    const urlMode = searchParams.get('mode')
    if (urlMode === 'signup' || urlMode === 'signin') {
      setMode(urlMode)
    }
  }, [searchParams])

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    let result
    if (mode === 'signup') {
      result = await signUpWithEmail(formData.email, formData.password, formData.displayName)
    } else if (mode === 'forgot') {
      result = await resetPassword(formData.email)
      if (!result.error) {
        setSuccess('Password reset email sent! Check your inbox.')
        setLoading(false)
        return
      }
    } else {
      result = await signInWithEmail(formData.email, formData.password)
    }

    setLoading(false)

    if (result.error) {
      setError(result.error)
    } else if (result.user) {
      // Redirect to intended destination or dashboard
      const from = location.state?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    }
  }

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true)
    setError('')

    const result = await signInWithGoogle()

    setGoogleLoading(false)

    if (result.error) {
      setError(result.error)
    } else if (result.user) {
      const from = location.state?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    }
  }

  const toggleMode = (newMode) => {
    setMode(newMode)
    setError('')
    setSuccess('')
    setFormData({ email: '', password: '', displayName: '' })
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold gradient-text">voxerface</h1>
          <p className="mt-2 text-dark-400">
            {mode === 'signup' && 'Create your account'}
            {mode === 'signin' && 'Welcome back'}
            {mode === 'forgot' && 'Reset your password'}
          </p>
        </div>

        <Card className="animate-fade-in">
          {/* Tab Switcher */}
          {mode !== 'forgot' && (
            <div className="flex mb-6 bg-dark-800 rounded-xl p-1">
              <button
                type="button"
                onClick={() => toggleMode('signin')}
                className={`
                  flex-1 py-2.5 text-sm font-medium rounded-lg transition-all
                  ${mode === 'signin' 
                    ? 'bg-dark-700 text-dark-100 shadow' 
                    : 'text-dark-400 hover:text-dark-200'
                  }
                `}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => toggleMode('signup')}
                className={`
                  flex-1 py-2.5 text-sm font-medium rounded-lg transition-all
                  ${mode === 'signup' 
                    ? 'bg-dark-700 text-dark-100 shadow' 
                    : 'text-dark-400 hover:text-dark-200'
                  }
                `}
              >
                Sign Up
              </button>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 text-sm">
              {success}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <Input
                label="Name"
                name="displayName"
                type="text"
                placeholder="Your name"
                value={formData.displayName}
                onChange={handleChange}
                autoComplete="name"
              />
            )}

            <Input
              label="Email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />

            {mode !== 'forgot' && (
              <Input
                label="Password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
            )}

            {/* Forgot Password Link */}
            {mode === 'signin' && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => toggleMode('forgot')}
                  className="text-sm text-accent-400 hover:text-accent-300 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <Button type="submit" fullWidth loading={loading}>
              {mode === 'signup' && 'Create Account'}
              {mode === 'signin' && 'Sign In'}
              {mode === 'forgot' && 'Send Reset Link'}
            </Button>
          </form>

          {/* Back to Sign In (from Forgot) */}
          {mode === 'forgot' && (
            <button
              type="button"
              onClick={() => toggleMode('signin')}
              className="w-full mt-4 text-sm text-dark-400 hover:text-dark-200 transition-colors"
            >
              ← Back to Sign In
            </button>
          )}

          {/* Divider */}
          {mode !== 'forgot' && (
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-dark-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-dark-900 text-dark-500">or continue with</span>
              </div>
            </div>
          )}

          {/* Google Sign In */}
          {mode !== 'forgot' && (
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={handleGoogleSignIn}
              loading={googleLoading}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google
            </Button>
          )}
        </Card>

        {/* Animated Line */}
        <div className="mt-8">
          <AnimatedLine trigger={true} delay={300} color="gradient" />
        </div>
      </div>
    </div>
  )
}

export default AuthView
